
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, RotateCcw, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface UserCredit {
  id: string;
  username: string | null;
  full_name: string | null;
  email: string;
  credits_balance: number;
  credits_used: number;
  subscription_type: string;
  total_token_usage: number;
  monthly_revenue: number;
  total_revenue: number;
}

interface OptimizedUserCreditsPanelProps {
  refreshTrigger?: number;
}

export default function OptimizedUserCreditsPanel({ refreshTrigger }: OptimizedUserCreditsPanelProps) {
  const [users, setUsers] = useState<UserCredit[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserCredit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [resettingUserId, setResettingUserId] = useState<string | null>(null);

  useEffect(() => {
    fetchUserCredits();
  }, [refreshTrigger]);

  useEffect(() => {
    // Filter users based on search term
    const filtered = users.filter(user => 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [users, searchTerm]);

  const fetchUserCredits = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.rpc('get_admin_users_data');
      
      if (error) {
        console.error('Database error:', error);
        if (error.message?.includes('permission denied')) {
          toast.error('Access denied. Admin role required.');
        } else if (error.message?.includes('function get_admin_users_data')) {
          toast.error('Database function error. Please check server logs.');
        } else {
          toast.error(`Failed to load user credits: ${error.message}`);
        }
        return;
      }
      
      setUsers(data || []);
      console.log('Successfully loaded', data?.length || 0, 'user credits');
    } catch (error: any) {
      console.error('Error fetching user credits:', error);
      toast.error(`Failed to load user credits: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const resetUserCredits = async (userId: string) => {
    try {
      setResettingUserId(userId);
      console.log('Resetting credits to 30 for user:', userId);
      toast.loading("Resetting user credits to 30...");
      
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      
      if (!accessToken) {
        throw new Error("Authentication required");
      }
      
      const { data, error } = await supabase.functions.invoke('admin-reset-credits', {
        body: { userId },
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      
      toast.dismiss();
      
      if (error) {
        console.error('Error from edge function:', error);
        throw error;
      }
      
      if (!data || !data.success) {
        throw new Error(data?.message || "Unknown error occurred");
      }
      
      toast.success("User credits reset to 30 successfully");
      fetchUserCredits();
    } catch (error: any) {
      console.error('Error resetting user credits:', error);
      toast.error(error.message || "Failed to reset user credits");
    } finally {
      setResettingUserId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getSubscriptionBadgeVariant = (type: string) => {
    switch (type) {
      case 'yearly': return 'default';
      case 'monthly': return 'secondary';
      case 'free': return 'outline';
      default: return 'outline';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-10 w-10 rounded-full border-2 border-t-transparent border-primary animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Credits Management</h2>
          <p className="text-muted-foreground">
            {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full sm:w-64"
            />
          </div>
          
          <Button variant="outline" onClick={fetchUserCredits} disabled={isLoading}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Table Section */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Subscription</TableHead>
              <TableHead>Credits Balance</TableHead>
              <TableHead>Credits Used</TableHead>
              <TableHead>Token Usage</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                  {searchTerm ? 'No users match your search criteria' : 'No users found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {user.full_name || user.username || 'Unnamed User'}
                      </div>
                      {user.username && user.full_name && (
                        <div className="text-sm text-muted-foreground">@{user.username}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{user.email}</TableCell>
                  <TableCell>
                    <Badge variant={getSubscriptionBadgeVariant(user.subscription_type)}>
                      {user.subscription_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{user.credits_balance}</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{user.credits_used}</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{user.total_token_usage.toLocaleString()}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resetUserCredits(user.id)}
                      disabled={resettingUserId === user.id}
                      className="gap-1 text-xs"
                    >
                      <RefreshCw className={cn("h-3.5 w-3.5", resettingUserId === user.id && "animate-spin")} />
                      Reset to 30 Credits
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
